import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info, Clipboard, Hash } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  title?: string;
  type: ToastType;
  duration: number;
  progress: number;
  copyable?: boolean;
  meta?: any;
  correlationId?: string;
}

interface ToastContextType {
  addToast: (
    msg: string,
    type?: ToastType,
    duration?: number,
    title?: string,
    copyable?: boolean,
    meta?: any,
    correlationId?: string,
    notify?: boolean,
  ) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastItem: React.FC<{
  toast: Toast;
  onRemove: (id: number) => void;
}> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [copied, setCopied] = useState(false);
  const [copiedCorrelation, setCopiedCorrelation] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setIsVisible(true), 50);

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - 100 / (toast.duration / 100);
        if (newProgress <= 0) {
          clearInterval(progressTimer);
          return 0;
        }
        return newProgress;
      });
    }, 100);

    const removeTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(removeTimer);
      clearInterval(progressTimer);
    };
  }, [toast.id, toast.duration, onRemove]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(toast.message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyCorrelationId = async () => {
    if (!toast.correlationId) return;
    await navigator.clipboard.writeText(toast.correlationId);
    setCopiedCorrelation(true);
    setTimeout(() => setCopiedCorrelation(false), 2000);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-6 h-6" />;
      case 'error':
        return <XCircle className="w-6 h-6" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6" />;
      case 'info':
        return <Info className="w-6 h-6" />;
    }
  };

  const getIconColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-amber-400';
      case 'info':
        return 'text-blue-400';
    }
  };

  const handlePerformDownload = () => {
    const url = toast.meta?.downloadUrl;
    if (!url) return;
    // Ask app to perform authenticated download (DashboardPage listens for this)
    window.dispatchEvent(new CustomEvent('performDownload', { detail: { url } }));
    // close toast
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-lg shadow-2xl
        bg-black border-2 border-red-500
        transform transition-all duration-300 ease-out min-w-[500px] max-w-md
        ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}
        pb-4
      `}
    >
      {/* Red timer border on top */}
      <div className="absolute top-0 left-0 h-1.5 bg-red-750 w-full">
        <div
          className="h-full bg-red-500 transition-all duration-100 ease-linear shadow-lg shadow-red-500/50"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className={`p-5 pr-14 pt-6`}>
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 ${getIconColor()}`}>{getIcon()}</div>
          <div className="flex-1 min-w-0">
            {toast.title && <p className="text-base font-bold text-white mb-1.5">{toast.title}</p>}
            <p className="text-base text-gray-300 leading-6">{toast.message}</p>

            {/* show correlation id line when present so users notice it */}
            {toast.correlationId && (
              <p className="mt-2 text-xs text-gray-400 select-all break-all">
                Correlation ID: <span className="font-mono text-xs text-gray-300">{toast.correlationId}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Download button shown at bottom center if toast contains a download URL */}
      {/* {toast.meta?.downloadUrl && (
        <div className="w-full flex justify-center pb-3">
          <button
            onClick={handlePerformDownload}
            className="px-4 py-2 bg-white text-black rounded-md font-semibold hover:bg-gray-100 transition"
            title="Download report"
          >
            Download Report
          </button>
        </div>
      )} */}

      {/* Bottom action buttons row with red dividers - only show if there are any actions */}
      {(toast.correlationId || toast.copyable || toast.meta?.downloadUrl) && (
        <div className="flex items-center justify-center gap-0">
          {/* Copy correlation id (if present) */}
          {toast.correlationId && (
            <>
              <button
                onClick={handleCopyCorrelationId}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white transition text-sm hover:text-gray-100"
                title={copiedCorrelation ? 'Copied!' : 'Copy correlation ID'}
              >
                <Clipboard className={`w-4 h-4 ${copiedCorrelation ? 'text-green-400' : 'text-gray-200'}`} />
                <span className="select-none">{copiedCorrelation ? 'Copied!' : 'Copy ID'}</span>
              </button>
              {(toast.copyable || toast.meta?.downloadUrl) && <div className="w-px h-8 bg-red-500"></div>}
            </>
          )}

          {/* Copy message (if copyable) */}
          {toast.copyable && (
            <>
              <button
                onClick={handleCopy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white transition text-sm hover:text-gray-100"
                title={copied ? 'Copied!' : 'Copy message'}
              >
                <Clipboard className={`w-4 h-4 ${copied ? 'text-green-400' : 'text-gray-200'}`} />
                <span className="select-none">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              {toast.meta?.downloadUrl && <div className="w-px h-8 bg-red-500"></div>}
            </>
          )}

          {/* Download button (if any) - placed last */}
          {toast.meta?.downloadUrl && (
            <button
              onClick={handlePerformDownload}
              className="flex-1 px-4 py-2 text-white transition text-sm font-semibold hover:text-gray-100"
              title="Download report"
            >
              Download
            </button>
          )}
        </div>
      )}

      {/* Close button - top right */}
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onRemove(toast.id), 300);
        }}
        className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-red-900/50 transition-colors duration-200"
        title="Close"
      >
        <X className="w-5 h-5 text-red-400 hover:text-red-300" />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration = 4000,
      title?: string,
      copyable: boolean = false,
      meta?: any,
      correlationId?: string,
      notify: boolean = true, // Default to true for backward compatibility
    ) => {
      const id = Date.now() + Math.random();
      const newToast: Toast = {
        id,
        message,
        title,
        type,
        duration,
        progress: 100,
        copyable,
        meta,
        correlationId,
      };

      setToasts((prev) => [...prev, newToast]);

      // Only save to notifications if notify is true
      if (notify) {
        window.dispatchEvent(
          new CustomEvent('saveToNotification', {
            detail: {
              message,
              title,
              type,
              expirationMinutes: 30,
              meta,
              correlationId,
            },
          }),
        );
      }
    },
    [],
  );

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed top-6 right-6 z-50 space-y-3 pointer-events-none">
        <div className="space-y-2">
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto">
              <ToastItem toast={toast} onRemove={removeToast} />
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
};
